import { Auth, MiniAuth } from 'types'
import { ALMANAC_CORE_WORKSPACE_ID } from './constants'
import { inSuperAdminMode } from './workspaceUtils'

/**
 * Build an Algolia filter string that restricts anything a user should never see
 * @param auth User auth object
 * @returns Filter string
 */
export const BuildDefaultString = (auth: Auth | null, contributorMode: boolean): string => {
  const authString = auth
    ? // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      `(viewable_by:${auth!.id} OR company:1 OR made_public:true)`
    : `(company:1 OR made_public:true)`
  const finalString = `${authString} AND ${contributorMode ? '(stub:true)' : '(stub:false)'}`
  return finalString
}

/**
 * Builds an Algolia filter string that restricts anything a user can see
 * For super admins, it doesn't restrict it (returns an empty filer)
 *
 * @param workspaceId number
 * @param auth MiniAuth
 * @returns string
 */
export const viewableByFilter = (workspaceId: number | undefined, auth: MiniAuth | null) => {
  const emptyResultFilter = 'viewable_by:x'
  const companyResultsFilter = `company:${workspaceId}`
  if (workspaceId) {
    if (auth) {
      return inSuperAdminMode(workspaceId, auth) ? companyResultsFilter : `viewable_by:${auth.id}`
    } else {
      return workspaceId === ALMANAC_CORE_WORKSPACE_ID ? companyResultsFilter : emptyResultFilter
    }
  } else {
    return emptyResultFilter
  }
}

/**
 * Builds an Algolia filter string for groups that restricts the groups shown for non super admin users
 *  - users can view non-closed groups (listed as private on front end) or groups they have a memberrhip to.
 *
 * @param workspaceId number
 * @param auth Auth
 * @returns string
 */
export const groupsViewableByFilter = (workspaceId: number | undefined, auth: Auth | null) => {
  const emptyResultFilter = 'viewable_by:x'
  const companyResultsFilter = `company:${workspaceId}`
  if (workspaceId && auth) {
    return inSuperAdminMode(workspaceId, auth) ? companyResultsFilter : `viewable_by:${auth.id}`
  }
  return emptyResultFilter
}

/**
 * Builds an Algolia filter string that restricts anything a user can edit
 * For super admins, it doesn't restrict it (returns an empty filer)
 *
 * @param workspaceId number
 * @param auth Auth
 * @returns string
 */
export const editableByFilter = (workspaceId: number | undefined, auth: Auth | null) => {
  const emptyResultFilter = 'editable_by:x'
  const allFilesFilter = `company:${workspaceId}`
  if (workspaceId && auth) {
    return inSuperAdminMode(workspaceId, auth) ? allFilesFilter : `editable_by:${auth.id}`
  } else {
    return emptyResultFilter
  }
}

export const filtersQuery = (operator: 'AND' | 'OR', filters: (String | boolean | undefined | null)[]) => {
  return filters.filter((x) => x).join(` ${operator} `)
}
